import type { ScopedResult } from 'instantsearch.js'
import { SEARCH_CONFIG } from '../../Config/Search'
import { logEvent } from '../analytics/firAnalytics'
import { ANALYTICS_TAGS } from '../../Constants/analytics'
import { trackSearchClick } from './searchInsights.utils'
import { safeNavigate } from '../navigation'

type PLLHitInternal = {
  objectID: string
  __indexName?: string
  title?: string
  image?: string
  hyperlink?: string
  position?: string
  teamName?: string
  date?: string
  type?: 'player' | 'team' | 'article' | 'video' | 'general'
}

type ShopifyHitInternal = {
  objectID: string
  __indexName?: string
  handle?: string
  title?: string
  image?: string
  price?: number
  product_type?: string
}

type CategoryResultsInternal = {
  players: PLLHitInternal[]
  news: PLLHitInternal[]
  teams: PLLHitInternal[]
  shop: ShopifyHitInternal[]
  general: PLLHitInternal[]
}

type ResultCountsInternal = {
  players: number
  news: number
  teams: number
  shop: number
  general: number
}

type UnifiedResult = {
  item: PLLHitInternal | ShopifyHitInternal
  category: 'players' | 'news' | 'teams' | 'shop' | 'general'
  score: number
}

const SHOP_BASE_URL = 'https://shop.premierlacrosseleague.com/products'

const CATEGORY_WEIGHT = {
  players: 4000,
  teams: 3000,
  general: 3000,
  news: 2000,
  shop: 1000,
}

export const addUniqueItem = <T extends { objectID: string }>(
  items: T[],
  seenIds: Set<string>,
  item: T
): void => {
  if (!seenIds.has(item.objectID)) {
    seenIds.add(item.objectID)
    items.push(item)
  }
}

export const parseSearchResults = <T extends CategoryResultsInternal>(
  scopedResults: ScopedResult[]
): T => {
  const result: CategoryResultsInternal = {
    players: [],
    news: [],
    teams: [],
    shop: [],
    general: [],
  }

  if (!scopedResults || scopedResults.length === 0) {
    return result as T
  }

  const seenPlayerIds = new Set<string>()
  const seenTeamIds = new Set<string>()
  const seenNewsIds = new Set<string>()
  const seenShopIds = new Set<string>()
  const seenGeneralIds = new Set<string>()

  for (const scopedResult of scopedResults) {
    const searchResult = scopedResult.results
    if (!searchResult?.hits) continue

    const indexName = scopedResult.indexId
    const hits = searchResult.hits

    switch (indexName) {
      case SEARCH_CONFIG.general:
        for (const h of hits) {
          addUniqueItem(result.general, seenGeneralIds, {
            ...(h as PLLHitInternal),
            type: 'general',
            __indexName: indexName,
          })
        }
        break
      case SEARCH_CONFIG.players:
        for (const h of hits) {
          addUniqueItem(result.players, seenPlayerIds, {
            ...(h as PLLHitInternal),
            type: 'player',
            __indexName: indexName,
          })
        }
        break
      case SEARCH_CONFIG.teams:
        for (const h of hits) {
          addUniqueItem(result.teams, seenTeamIds, {
            ...(h as PLLHitInternal),
            type: 'team',
            __indexName: indexName,
          })
        }
        break
      case SEARCH_CONFIG.posts:
        for (const h of hits) {
          addUniqueItem(result.news, seenNewsIds, {
            ...(h as PLLHitInternal),
            type: 'article',
            __indexName: indexName,
          })
        }
        break
      case SEARCH_CONFIG.videos:
        for (const h of hits) {
          addUniqueItem(result.news, seenNewsIds, {
            ...(h as PLLHitInternal),
            type: 'video',
            __indexName: indexName,
          })
        }
        break
      case SEARCH_CONFIG.shop:
        for (const h of hits) {
          addUniqueItem(result.shop, seenShopIds, {
            ...(h as ShopifyHitInternal),
            __indexName: indexName,
          })
        }
        break
    }
  }

  return result as T
}

/**
 * Merges all results from different categories and sorts by relevancy.
 * @param results - The results to merge and sort.
 * @returns The merged and sorted results.
 */
export const mergeAndSortByRelevancy = (
  results: CategoryResultsInternal
): UnifiedResult[] => {
  const unified: UnifiedResult[] = []

  results.players.forEach((item, index) => {
    unified.push({
      item,
      category: 'players',
      score: CATEGORY_WEIGHT.players - index,
    })
  })

  const sortedNews = [...results.news].sort((a, b) => {
    const dateA = a.date ? new Date(a.date).getTime() : 0
    const dateB = b.date ? new Date(b.date).getTime() : 0
    const safeDateA = Number.isFinite(dateA) ? dateA : 0
    const safeDateB = Number.isFinite(dateB) ? dateB : 0
    return safeDateB - safeDateA
  })

  results.general.forEach((item, index) => {
    unified.push({
      item,
      category: 'general',
      score: CATEGORY_WEIGHT.general - index,
    })
  })

  sortedNews.forEach((item, index) => {
    unified.push({
      item,
      category: 'news',
      score: CATEGORY_WEIGHT.news - index,
    })
  })

  results.shop.forEach((item, index) => {
    unified.push({
      item,
      category: 'shop',
      score: CATEGORY_WEIGHT.shop - index,
    })
  })

  results.teams.forEach((item, index) => {
    unified.push({
      item,
      category: 'teams',
      score: CATEGORY_WEIGHT.teams - index,
    })
  })

  return unified.sort((a, b) => b.score - a.score)
}

export const calculateResultCounts = <T extends ResultCountsInternal>(
  results: CategoryResultsInternal
): T =>
  ({
    players: results.players.length,
    news: results.news.length,
    teams: results.teams.length,
    shop: results.shop.length,
    general: results.general.length,
  } as T)

export const getTotalResultCount = (counts: ResultCountsInternal): number =>
  counts.players + counts.news + counts.teams + counts.shop + counts.general

export const getQueryID = (
  scopedResults: ScopedResult[],
  indexName: string
): string | undefined => {
  const scopedResult = scopedResults.find((sr) => sr.indexId === indexName)
  return scopedResult?.results?.queryID
}

export const getPositionInIndex = (
  scopedResults: ScopedResult[],
  indexName: string,
  objectID: string
): number | undefined => {
  const scopedResult = scopedResults.find((sr) => sr.indexId === indexName)
  if (!scopedResult?.results?.hits) return undefined

  const position = scopedResult.results.hits.findIndex(
    (hit) => hit.objectID === objectID
  )
  return position >= 0 ? position + 1 : undefined
}

export const handlePLLResultSelect = (
  item: PLLHitInternal,
  onClose: () => void,
  scopedResults?: ScopedResult[]
): void => {
  logEvent(ANALYTICS_TAGS.search_click, {
    index: item.__indexName,
    objectID: item.objectID,
    type: item.type || 'general',
  })

  if (item.__indexName && scopedResults) {
    const queryID = getQueryID(scopedResults, item.__indexName)
    const position = getPositionInIndex(scopedResults, item.__indexName, item.objectID)

    if (position !== undefined) {
      trackSearchClick(item.__indexName, item.objectID, position, queryID)
    }
  }

  if (item.hyperlink) {
    safeNavigate(item.hyperlink)
  }
  onClose()
}

export const handleShopResultSelect = (
  item: ShopifyHitInternal,
  onClose: () => void,
  scopedResults?: ScopedResult[]
): void => {
  logEvent(ANALYTICS_TAGS.search_click, {
    index: item.__indexName,
    objectID: item.objectID,
    type: 'shop',
  })

  if (item.__indexName && scopedResults) {
    const queryID = getQueryID(scopedResults, item.__indexName)
    const position = getPositionInIndex(scopedResults, item.__indexName, item.objectID)

    if (position !== undefined) {
      trackSearchClick(item.__indexName, item.objectID, position, queryID)
    }
  }

  if (item.handle) {
    safeNavigate(`${SHOP_BASE_URL}/${encodeURIComponent(item.handle)}`)
  }
  onClose()
}

export const highlightText = (
  text: string,
  query: string
): { text: string; isMatch: boolean }[] => {
  if (!query || !text) {
    return [{ text, isMatch: false }]
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part) => ({
    text: part,
    isMatch: part.toLowerCase() === query.toLowerCase(),
  }))
}
